import { HttpMethod } from '@/libs/http/http.types';
import { parseJsonBytesOrUndefined } from '@/libs/http/response.utils';
import { Logger } from '@/libs/logger/logger';
import type { Pubky } from '@/models/models.types';
import { type SettingsJson, SettingsNormalizer } from '@/pipes/settings/settings.normalizer';
import { HomeserverService } from '@/services/homeserver/homeserver';
import { retryHomeserverWriteAfterReread, type THomeserverWriteFence } from '@/services/homeserver/write-retry';
import type { SettingsState } from '@/stores/settings/settings.types';

/**
 * Settings application service.
 *
 * Handles synchronization of user settings between the local store and homeserver.
 * Settings are stored at: pubky://{pubky}/priv/app.pubky/v1/settings.json. An account whose only
 * settings are the 0.x ones (`/pub/pubky.app/settings.json`, which the migrator does not copy)
 * reads those until its first write to the v1 file; the first `initializeSettings` after the
 * migration gate writes it, and the public 0.x file is left in place (D15).
 */
export class SettingsApplication {
  private static pendingCommits = new Map<Pubky, Promise<void>>();

  private constructor() {}

  /**
   * Commits settings update to the homeserver.
   *
   * Settings are replaced as one document, so a client whose local store predates
   * `privacy.moderationBot` would erase it (and trigger a spurious re-follow on the next
   * sign-in). When the local state lacks the field, the remote value is carried over first.
   * This self-limits: once the store has learned the field, no extra read happens.
   *
   * A failed read rejects the write rather than pushing blind. Settings are local-first, so the
   * change stays in the persisted store and the user keeps seeing it; it reaches the homeserver
   * only if the user changes settings again, since sign-in resets the store and session restore
   * does not sync settings. Dropping one toggle beats erasing the field and re-following the bot.
   *
   * A PUT the homeserver refuses with a retried status (429, 500, 503 or the publish conflict)
   * starts over from the read, since the remote document may have changed meanwhile.
   *
   * @param settings, The current settings state to persist
   * @param pubky, The user's public key
   * @param signal, Optional cancellation signal checked when this queued write starts
   */
  static async commitUpdate(settings: SettingsState, pubky: Pubky, signal?: AbortSignal): Promise<void> {
    await this.enqueueWrite(pubky, async () => {
      if (signal?.aborted) return;

      const fence = HomeserverService.createWriteFence();
      await retryHomeserverWriteAfterReread(fence, async () => {
        if (signal?.aborted) return;
        let resolved = settings;
        if (settings.privacy.moderationBot === undefined) {
          const remote = await this.fetchFromHomeserver(pubky);
          if (signal?.aborted) return;
          resolved = this.withRemoteModerationBot(settings, remote);
        }

        await this.push(resolved, pubky, fence);
      });
    });
  }

  /** Returns local settings enriched with the remote processed-bot state when only the remote has it. */
  private static withRemoteModerationBot(settings: SettingsState, remote: SettingsState | null): SettingsState {
    const moderationBot = remote?.privacy.moderationBot;
    if (settings.privacy.moderationBot !== undefined || moderationBot === undefined) return settings;

    Logger.info('[Settings] Carrying remote moderation-bot state into local write');
    return { ...settings, privacy: { ...settings.privacy, moderationBot } };
  }

  /** Serializes homeserver writes per account so concurrent full-document replaces cannot interleave. */
  private static async enqueueWrite(pubky: Pubky, write: () => Promise<void>): Promise<void> {
    const previousCommit = this.pendingCommits.get(pubky) ?? Promise.resolve();
    const pendingCommit = previousCommit.catch(() => {}).then(write);

    this.pendingCommits.set(pubky, pendingCommit);

    try {
      await pendingCommit;
    } finally {
      if (this.pendingCommits.get(pubky) === pendingCommit) {
        this.pendingCommits.delete(pubky);
      }
    }
  }

  private static async push(settings: SettingsState, pubky: Pubky, fence: THomeserverWriteFence): Promise<void> {
    const { settings: settingsJson, meta } = SettingsNormalizer.to(settings, pubky);

    Logger.info('[Settings] Pushing to homeserver', { url: meta.url, settings: settingsJson });

    await HomeserverService.request({
      method: HttpMethod.PUT,
      url: meta.url,
      bodyJson: settingsJson as unknown as Record<string, unknown>,
      fence,
    });

    Logger.info('[Settings] Push complete');
  }

  /**
   * Fetches settings from the homeserver: the v1 file, else the 0.x one.
   * Returns null if settings don't exist on homeserver (404).
   *
   * @param pubky, The user's public key
   * @returns The settings state from homeserver, or null if not found
   */
  static async fetchFromHomeserver(pubky: Pubky): Promise<SettingsState | null> {
    return (await this.fetchRemote(pubky))?.settings ?? null;
  }

  /**
   * The stored settings and where they were read. The 0.x file is read only while the v1 one is
   * absent, so once the v1 file exists a newer 0.x write (a stale 0.x tab) is never adopted.
   */
  private static async fetchRemote(pubky: Pubky): Promise<{ settings: SettingsState; legacy: boolean } | null> {
    const settings = await this.fetchSettingsAt(SettingsNormalizer.buildUrl(pubky));
    if (settings) return { settings, legacy: false };

    const legacySettings = await this.fetchSettingsAt(SettingsNormalizer.buildLegacyUrl(pubky));
    return legacySettings ? { settings: legacySettings, legacy: true } : null;
  }

  private static async fetchSettingsAt(url: string): Promise<SettingsState | null> {
    Logger.info('[Settings] Pulling from homeserver', { url });

    // A 404 is the usual answer until the first write (a new or just-migrated account), so it is
    // read as absent without reporting an error; any other failure still rejects.
    const stored = await HomeserverService.getBytesIfExists(url);
    if (!stored) {
      Logger.info('[Settings] Pull complete, settings file not found (404)');
      return null;
    }

    const settingsJson = parseJsonBytesOrUndefined<SettingsJson>(stored.bytes);
    if (!settingsJson) {
      Logger.warn('[Settings] Pull complete, the settings file holds no JSON document', { url });
      return null;
    }

    const settings = SettingsNormalizer.from(settingsJson);
    Logger.info('[Settings] Pull complete', { settings });
    return settings;
  }

  /**
   * Initializes settings on bootstrap.
   * Fetches settings from homeserver and merges with local settings.
   * Uses version and timestamp for conflict resolution (newer wins).
   * A push the homeserver refuses with a retried status starts over from the read: the remote
   * document may have changed after the comparison and may now be the newer one.
   * Whichever side wins is written to the v1 file when the remote came from the 0.x file, so
   * the first sync after the migration gate copies the settings into the app's namespace.
   *
   * @param pubky, The user's public key
   * @returns The settings the store should adopt: remote when newer, or local enriched with
   *   remote-only moderation-bot state; null when the store is already current
   * @throws If fetch or sync operations fail, caller should handle errors
   */
  static async initializeSettings(pubky: Pubky, localSettings: SettingsState): Promise<SettingsState | null> {
    Logger.info('[Settings] Initializing settings sync');
    const fence = HomeserverService.createWriteFence();
    return await retryHomeserverWriteAfterReread(fence, () => this.reconcileWithRemote(pubky, localSettings, fence));
  }

  private static async reconcileWithRemote(
    pubky: Pubky,
    localSettings: SettingsState,
    fence: THomeserverWriteFence,
  ): Promise<SettingsState | null> {
    const remote = await this.fetchRemote(pubky);

    if (!remote) {
      Logger.info('[Settings] No remote settings, pushing local to homeserver');
      // Stamp a real timestamp if updatedAt is 0 (initial default), so homeserver has a valid timestamp for future conflict resolution
      const settingsWithTimestamp = { ...localSettings, updatedAt: localSettings.updatedAt || Date.now() };
      await this.enqueueWrite(pubky, () => this.push(settingsWithTimestamp, pubky, fence));
      return settingsWithTimestamp;
    }

    const remoteSettings = remote.settings;
    // Check if remote settings are newer (higher version or same version with newer timestamp)
    const isRemoteNewer =
      remoteSettings.version > localSettings.version ||
      (remoteSettings.version === localSettings.version && remoteSettings.updatedAt > localSettings.updatedAt);

    Logger.info('[Settings] Comparing versions', {
      local: { version: localSettings.version, updatedAt: localSettings.updatedAt },
      remote: { version: remoteSettings.version, updatedAt: remoteSettings.updatedAt },
      isRemoteNewer,
    });

    if (isRemoteNewer) {
      Logger.info('[Settings] Using remote settings (newer)');
      if (remote.legacy) {
        // The 0.x file is the only copy: write it to the v1 file. A retried status re-runs the read
        // above, so a v1 file another client wrote meanwhile is adopted instead of overwritten.
        Logger.info('[Settings] Copying the 0.x settings to the app namespace');
        await this.enqueueWrite(pubky, () => this.push(remoteSettings, pubky, fence));
      }
      return remoteSettings;
    }

    Logger.info('[Settings] Local settings newer, pushing to homeserver');
    // The remote document was just read; reuse it instead of probing again inside commitUpdate.
    const settingsToPush = this.withRemoteModerationBot(localSettings, remoteSettings);
    await this.enqueueWrite(pubky, () => this.push(settingsToPush, pubky, fence));
    return settingsToPush === localSettings ? null : settingsToPush;
  }
}
